import {
  ChangeDetectionStrategy, Component, DestroyRef, ElementRef, Injector,
  computed, effect, inject, input, signal, untracked, viewChild,
  type AnimationCallbackEvent,
} from '@angular/core';
import { enterMotion, leaveMotion, motionTokens, play, resolveMotion } from '../../../../core/motion';
import { PopoverBandService } from '../../../../core/overlays/popover-band.service';
import { FormsModule } from '@angular/forms';
import { PaperProfilePanelComponent, type PaperProfileAction } from '@ricoh-paper/angular/shell';
import { Router } from '@angular/router';
import {
  PaperIconComponent,
  PaperButtonComponent,
  PaperAvatarComponent,
  PaperSegmentedControlComponent,
  PaperSkeletonComponent,
  PaperTagComponent,
  PaperToastService,
  PaperToggleComponent,
  type PaperSegmentedControlOption,
} from '@ricoh-paper/angular';
import { ShellStateService } from '../../../../core/services/shell-state.service';
import { ProfileDataService } from '../../../../core/services/profile-data.service';
import { ProfileApiService } from '../../../../core/services/profile-api.service';
import { BookingService } from '../../../../core/services/booking.service';
import { IssuesApiService } from '../../../../core/services/issues-api.service';
import { SessionService } from '../../../../core/services/session.service';
import { PushRegistrationService } from '../../../../core/services/push-registration.service';
import { PwaInstallService } from '../../../../core/services/pwa-install.service';
import { ViewerIdentityService } from '../../../../core/services/viewer-identity.service';
import { PlatformCustomerService } from '../../../../core/services/platform-customer.service';
import { MapPreferencesSyncService } from '../../../../core/services/map-preferences-sync.service';
import { ProfileSettingsSyncService } from '../../../../core/services/profile-settings-sync.service';
import { AppUpdateService } from '../../../../core/services/app-update.service';
import { AnalyticsService } from '../../../../core/services/analytics.service';
import { ANALYTICS_EVENTS } from '../../../../core/analytics/events';
import { logError } from '../../../../core/utils/error-sanitizer';
import { LocaleService } from '../../../../core/services/locale.service';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { FeatureFlagsViewerService } from '../../../../core/services/feature-flags-viewer.service';
import { FeatureFlagsService } from '../../../../core/services/feature-flags.service';
import { HelpUiService } from '../../../help/help-ui.service';
import { HelpMessengerService } from '../../../help/help-messenger.service';
import { HelpSupportService } from '../../../help/help-support.service';
import { FeedbackUiService } from '../../../../core/services/feedback-ui.service';
import { ReleasesService } from '../../../../core/services/releases.service';
import { AskCompanionPrefsService } from '../../../ask-companion/ask-companion-prefs.service';
import { AskCompanionService } from '../../../ask-companion/ask-companion.service';
import { ASK_COMPANION_FLAG } from '../../../ask-companion/ask-companion.model';
import { mediaQuerySignal } from '../../../ask-companion/ask-companion-motion';
import { ImFeatureFlagDirective } from '../../../../shared/directives/im-feature-flag.directive';
import {
  SWITCHER_SEARCH_THRESHOLD,
  filterCustomers,
  shouldShowSearch,
  shouldUseDialog,
} from '../workspace-switcher-dialog/workspace-switcher-filter.util';
import { WorkspaceSwitcherDialogService } from '../workspace-switcher-dialog/workspace-switcher-dialog.service';
import { viewerIdentity } from './profile-identity';
import { PROFILE_PANEL_FLAGS } from './profile-panel-flags';
import {
  selectedPresenceTile,
  statusNameForTile,
  type PresenceTile,
} from './presence-tiles';

/**
 * Copy that `41-profile-panel` shows but `public/i18n/en.json` has no key for.
 *
 * Rendered as literal British English rather than as a `| transloco` call
 * against a key that does not exist (which would paint the raw key into the
 * UI). Collected here so the migration is one edit in one place once the
 * keys land — the exact keys required are listed in this lane's report.
 * Every OTHER string on this surface goes through Transloco.
 */
const PENDING_I18N = {
  whereYouAreToday: 'Where you are today',
  planTheRestOfYourWeek: 'Plan the rest of your week',
  viewProfile: 'View profile',
  // `41-profile-panel` captions this row "Theme, font, language", and
  // `navigateToPreferences()` goes to `/profile/appearance`, which carries
  // theme, font and brand colour and NO language control — the locale lives
  // on `/profile/regional`. A caption naming a control the destination does
  // not hold is a promise the next click breaks, so this names what is
  // actually there. OWED: fold the locale picker into `/profile/appearance`
  // (or point this row at a Preferences index that holds all three) and the
  // reference wording becomes true rather than aspirational.
  preferencesHint: 'Theme, font, brand colour',
  avatarStyle: 'Avatar style',
  avatarStylePhoto: 'Photo',
  // The reference labels this tile "Avatar" against a generated mark. This
  // app's mark IS the viewer's initials and always has been, so the label
  // says which of the two you get.
  avatarStyleInitials: 'Initials',
  visibleToColleagues: 'Visible to colleagues',
  visibleToColleaguesHint: 'They can see where you are today',
  // Refusals. Every one of these replaces a silent no-op or, worse, a silent
  // write that the tile then contradicted.
  deskBookedBlocksStatus:
    'You have a desk booked today, so colleagues see you as in the office. '
    + 'Change or cancel the desk to say something else.',
  statusNotOffered: 'Your workspace has not enabled this status.',
  officeNotSet: 'In the office could not be set for today. Try again.',
  deskBookingUnavailable: 'Desk booking is not available in your workspace.',
} as const;

/**
 * How long the workspace filter sits still before it reports a search.
 *
 * Carried over from the header switcher verbatim. It is a debounce, not a
 * throttle: one event per SEARCH, not one per keystroke, so `query_length`
 * describes what the user actually looked for rather than every prefix of it.
 */
const WORKSPACE_SEARCH_DEBOUNCE_MS = 400;

/**
 * "What's new" counts once a session, wherever its dot showed. The key is the
 * one the retired floating Feedback button used, so a session that saw the
 * dot there before a reload is not counted again here.
 */
const WHATS_NEW_DOT_FIRED_KEY = 'whats-new.dot-shown-fired';

/**
 * im-profile-panel — the §10 profile dropdown.
 *
 * ONE JOB: who you are. §10's test for anything on this surface is "is this
 * about who I am, or about what I need to do?", and the latter belongs in
 * Bookings or Home's week. This panel used to fail that test comprehensively:
 * it carried a day-at-a-glance section, today's calendar, a who's-in-the-
 * office browser, the full eight-theme/four-font/brand picker and a language
 * switcher — a profile DASHBOARD wearing the name of a dropdown. Those
 * sections are gone from the template (the components are still on disk).
 * What is left is the reference screen: identity, where you are today,
 * appearance, presence visibility, and a short menu out.
 *
 * PRESENCE AND THE DESK ARE ONE DECISION (§10). Saying "In office" implies a
 * desk, so the tile is derived from today's desk booking and the row beneath
 * states which desk it is and offers Change — or Book a desk when the tile is
 * picked with nothing booked. The mapping lives in `presence-tiles.ts` and is
 * unit-tested; see that file for why the booking outranks a manual status.
 *
 * FAMILY: a POPOVER (`POPOVERS.md` §1), not a column. It was built into the
 * shell's full-height right-panel slot, which is the other family's rules
 * applied to this one — §1's opening line is that most overlay bugs in this
 * project were exactly that. The root now carries `data-popover`, so the
 * band owns click-away, Escape ordering, mutual exclusion and focus return;
 * the box is §2.4's documented no-measurement exception, in the SCSS. It
 * mounts at the root of `app-shell.component.html`, NOT in the slot.
 *
 * A11Y: the presence tiles are a real `role="radiogroup"` of buttons with
 * `aria-checked`; the visibility switch is `paper-toggle` (a CVA, hence
 * FormsModule) with its own accessible name because its caption sits beside
 * it rather than inside it.
 */
@Component({
  selector: 'im-profile-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    PaperIconComponent,
    PaperButtonComponent,
    PaperAvatarComponent,
    PaperProfilePanelComponent,
    PaperSegmentedControlComponent,
    PaperSkeletonComponent,
    PaperTagComponent,
    PaperToggleComponent,
    TranslocoPipe,
    ImFeatureFlagDirective,
  ],
  templateUrl: './profile-panel.component.html',
  styleUrl: './profile-panel.component.scss',
})
export class ProfilePanelComponent {
  protected readonly shell = inject(ShellStateService);
  protected readonly profileData = inject(ProfileDataService);
  protected readonly localeService = inject(LocaleService);
  protected readonly pwaInstall = inject(PwaInstallService);
  protected readonly copy = PENDING_I18N;
  private readonly router = inject(Router);
  private readonly bookingService = inject(BookingService);
  private readonly issuesApi = inject(IssuesApiService);
  private readonly sessionService = inject(SessionService);
  private readonly pushRegistration = inject(PushRegistrationService);
  private readonly profileApi = inject(ProfileApiService);
  private readonly viewerIdentity = inject(ViewerIdentityService);
  protected readonly platformCustomers = inject(PlatformCustomerService);
  private readonly mapPrefsSync = inject(MapPreferencesSyncService);
  private readonly profileSettingsSync = inject(ProfileSettingsSyncService);
  private readonly analytics = inject(AnalyticsService);
  private readonly toast = inject(PaperToastService);
  protected readonly appUpdate = inject(AppUpdateService);
  private readonly transloco = inject(TranslocoService);
  /** Read directly as well as through `*imFeatureFlag`: the template gate hides
   *  a dead CTA, this one stops a NON-template caller (the office tile) walking
   *  into the same redirect. */
  private readonly featureFlags = inject(FeatureFlagsViewerService);
  private readonly posthogFlags = inject(FeatureFlagsService);
  private readonly injector = inject(Injector);
  private readonly feedbackUi = inject(FeedbackUiService);
  protected readonly releases = inject(ReleasesService);
  private readonly companionPrefs = inject(AskCompanionPrefsService);

  // ── The Ask companion's switch (behind `ask-dock`) ──────────────────────
  // Nath (4 Oct 2026): "if you hit the profile icon in the top right, you
  // should be able to turn this on or off." The same choice as its own menu,
  // Settings › Appearance and map, and ⌘K: kept on this device. The companion
  // itself is fetched only when the switch is flipped; the row reads its
  // stored choice.

  /** Offered while the companion is: the flag on, and Ask available to this viewer. */
  protected readonly askCompanionOffered = computed(() =>
    this.posthogFlags.flagEnabled(ASK_COMPANION_FLAG) && this.featureFlags.isEnabled('ai')());
  protected readonly askCompanionOn = this.companionPrefs.visible;

  protected onAskCompanionChange(on: boolean): void {
    const companion = this.injector.get(AskCompanionService);
    if (on) companion.show();
    else companion.hide('profile-menu');
  }

  // ── Send feedback (moved here from the floating button, 4 Oct 2026) ─────
  // Nath: "Feedback is for beta users whilst we're being pre-launch … put that
  // behind the profile as well because it gets in the way." The same viewers
  // as the button (it never showed on a phone), the same panel and flow
  // (`PaperFeedbackWidgetComponent`, opened through `FeedbackUiService`) and
  // the same analytics; the What's New dot the button carried moves here.

  private readonly compactViewport = mediaQuerySignal('(max-width: 768px)');
  protected readonly feedbackOffered = computed(() => !this.compactViewport());
  protected readonly whatsNewHint = computed(() => {
    this.localeService.locale();
    const count = this.releases.unreadCount();
    return this.transloco.translate(count === 1 ? 'shared.feedback.trigger_title_one' : 'shared.feedback.trigger_title_other', { count });
  });

  /** Help centre is a page; Get Help opens authenticated support over the current route. */
  private readonly helpUi = inject(HelpUiService);
  protected readonly helpMessenger = inject(HelpMessengerService);
  /** Get Help shows only once Messenger has answered ready (never on the desktop). */
  protected readonly helpSupport = inject(HelpSupportService);
  /** With Help entitled and Contact support on, ask once whether Messenger is ready. Otherwise nothing is sent. */
  private readonly helpMessengerChecked = effect(() => {
    if (!this.featureFlags.isEnabled(PROFILE_PANEL_FLAGS.helpCentre)()) return;
    if (!this.featureFlags.isEnabled(PROFILE_PANEL_FLAGS.helpSupport)()) return;
    untracked(() => void this.helpSupport.checkMessenger());
  });
  protected async getHelp(): Promise<void> {
    this.analytics.capture(ANALYTICS_EVENTS.HELP_CENTRE_ACTION, { action: 'support', source: 'profile' });
    const opened = await this.helpMessenger.open(() => this.shell.closeRightPanel());
    this.analytics.capture(ANALYTICS_EVENTS.HELP_CENTRE_ACTION, { action: opened ? 'support_ready' : 'support_unavailable', source: 'profile' });
    if (!opened && this.helpMessenger.error()) this.refuse('profile-help-unavailable', this.transloco.translate(this.helpMessenger.error()));
  }
  protected openHelp(): void {
    this.shell.closeRightPanel();
    void this.helpUi.open();
  }

  protected openFeedback(): void {
    this.shell.closeRightPanel();
    this.feedbackUi.open();
  }

  /** The dot beside Send feedback was seen: once a session, as the button's was. */
  private readonly whatsNewDotSeen = effect(() => {
    const count = this.releases.unreadCount();
    if (!this.feedbackOffered() || count === 0) return;
    untracked(() => {
      try {
        if (sessionStorage.getItem(WHATS_NEW_DOT_FIRED_KEY)) return;
        sessionStorage.setItem(WHATS_NEW_DOT_FIRED_KEY, '1');
      } catch {
        // Sandboxed storage: count it; one extra event beats missing the cohort.
      }
      this.analytics.capture(ANALYTICS_EVENTS.WHATS_NEW_DOT_SHOWN, { unread_count: count, surface: 'profile-menu' });
    });
  });

  /**
   * RETAINED FOR THE ADMIN SHELL ONLY. `admin-shell.component.html` binds
   * `[showPin]="false"`, and that file is outside this lane, so the input
   * cannot be deleted without breaking its template compile. It no longer
   * does anything: the pin is gone from every mount because the profile
   * panel is overlay-only. Remove the binding and then this input.
   */
  readonly showPin = input(true);

  /** Reload onto the already-announced newer build (profile-panel row). */
  protected applyAppUpdate(): void {
    void this.appUpdate.applyUpdate('profile');
  }

  protected readonly avatarOverlayOpen = signal(false);
  protected readonly uploadingPhoto = signal(false);

  // ── Motion inside the panel (the panel's own opening is the shell's) ──
  // The photo dialog grows out of the avatar that opened it and goes back into
  // it, its scrim fading alongside; the desk row, the workspace list and its
  // error come and go in place (the kit's presence, docs/MOTION.md).
  private readonly avatarButton = viewChild<ElementRef<HTMLElement>>('avatarButton');
  private readonly avatarPop = enterMotion({ motion: 'pop', within: '.pp-avatar-modal', anchor: () => this.avatarButton()?.nativeElement });
  private readonly avatarRecede = leaveMotion({ within: '.pp-avatar-modal', anchor: () => this.avatarButton()?.nativeElement, scale: 0.96 });
  protected avatarDialogEnter(event: AnimationCallbackEvent): void {
    play(event.target, [{ opacity: 0 }, { opacity: 1 }], {
      duration: resolveMotion('pop').duration,
      easing: motionTokens().curve.ease,
      channel: 'pp-avatar-scrim',
    });
    this.avatarPop(event);
  }
  protected avatarDialogLeave(event: AnimationCallbackEvent): void {
    const m = resolveMotion('recede');
    play(event.target, [{ opacity: 1 }, { opacity: 0 }], {
      duration: m.duration,
      easing: m.easing,
      fill: 'forwards',
      channel: 'pp-avatar-scrim',
    });
    this.avatarRecede(event);
  }
  protected readonly rowIn = enterMotion();
  protected readonly rowOut = leaveMotion({ motion: 'collapse' });
  private readonly popoverBand = inject(PopoverBandService);
  /** The workspace list is one row until asked for, as in the reference. */
  protected readonly workspaceListOpen = signal(false);

  /** Coarse admin gate — matches the adminGuard semantics. Uses
   *  `canManageSomething()` because real admin users don't hold the
   *  literal `'*'` or `'admin:*'` action; they hold concrete
   *  `customer:manage`, `space:manage`, etc. Without this match the
   *  Settings entry on the profile panel disappears for every admin. */
  protected readonly isAdmin = computed(
    () => this.profileData.canManageSomething(),
  );

  // ── Identity ────────────────────────────────────────────

  /**
   * Who the viewer is, or `null` when that is NOT KNOWN.
   *
   * Was three computeds with three guesses — `?? 'User'`, `?? '?'`, `?? ''` —
   * so the panel opened before `loadAll()` resolved (or after a failed profile
   * query) rendered a fabricated identity in the viewer's own name slot. The
   * derivation and the reasoning now live in `profile-identity.ts`, next to
   * its unit tests.
   */
  protected readonly identity = computed(() =>
    viewerIdentity(this.profileData.profile()),
  );

  /**
   * Drives the header skeleton. Deliberately NOT `identity() === null`: a
   * skeleton asserts "a fetch is in flight", and standing there forever after
   * a failed load is its own small lie. When the identity is unknown and
   * nothing is being fetched, the header renders nothing at all.
   */
  protected readonly identityLoading = computed(
    () => this.identity() === null && this.profileData.loading(),
  );

  /**
   * The viewer's name, or `null`. Read by the avatar aria-label and the photo
   * overlay, both of which are only reachable from inside the identity gate.
   */
  protected readonly userName = computed(() => this.identity()?.name ?? null);

  protected readonly locationName = this.profileData.locationName;
  protected readonly currentStatus = this.profileData.derivedStatus;
  protected readonly profilePicture = computed(() => this.profileData.profile()?.picture);

  // ── Where you are today ─────────────────────────────────

  /**
   * Does the viewer hold a desk today, as the AVATAR DOT understands it?
   *
   * Read off `derivedStatus.isFromBooking` — literally the same computed the
   * dot 40px above this row renders — rather than off
   * `ProfileDataService.deskBooking`.
   *
   * That distinction is the whole point. `deskBooking` is
   * `find(b => b.bookingType === 'desk')` over today's bookings with NO time
   * filter; `derivedStatus` counts only a desk that is live or still to come.
   * A desk that ENDED earlier today satisfied the first and not the second, so
   * the tile said "In office" while the dot beside it had already fallen back
   * to the manual status — two assertions about the same day, in opposite
   * directions, one line apart. The comment that used to sit here claimed that
   * could never happen. It could; this is why it now cannot.
   */
  protected readonly hasDeskToday = computed(
    () => this.currentStatus().isFromBooking,
  );

  /**
   * Which desk. `null` means the name is NOT KNOWN — a booking with no space
   * on it — and the row then shows no name rather than captioning a real
   * booking "No desk booked".
   */
  protected readonly deskName = computed(
    () => this.currentStatus().bookingName ?? null,
  );

  /** Which of the three tiles is lit. `null` = the viewer has said nothing. */
  protected readonly presence = computed<PresenceTile | null>(() =>
    selectedPresenceTile({
      hasDeskToday: this.hasDeskToday(),
      inOfficeToday: this.profileData.inOfficeToday(),
      statusName: this.profileData.currentApiStatus()?.statusName ?? null,
    }),
  );

  /**
   * The flag keys this panel gates on, as COMPILE-CHECKED constants.
   *
   * `*imFeatureFlag` does not type-check a bare string literal — its
   * `FeatureFlagKey` input ends in `| (string & {})`, so every string is
   * assignable and a typo'd flag silently evaluates OFF, permanently hiding
   * the CTA it guards. `profile-panel-flags.ts` carries the full explanation
   * and the `satisfies` constraint that turns a typo into a build error.
   */
  protected readonly flag = PROFILE_PANEL_FLAGS;

  /** `/map` is behind `maps.wayfinding`; `/home/my-week` behind
   *  `platform.my-week`. Both redirect to the home page when off, so an
   *  ungated CTA closes the panel and dumps the viewer on the home page. */
  protected readonly canBookDesk = computed(() =>
    this.featureFlags.is(PROFILE_PANEL_FLAGS.mapsWayfinding),
  );

  /** Canonical status names this customer actually offers. */
  private readonly availableStatusNames = computed(() =>
    this.profileData.availableStatuses().map(s => s.name),
  );

  /**
   * Pick a tile.
   *
   * Office is not a status — it is a desk (§10). Picking it with a desk
   * already booked is a no-op; picking it without one clears any manual
   * status and hands the viewer to the map, where desks are booked.
   *
   * NOTHING HERE WRITES SILENTLY. A desk booking outranks a manual status in
   * `selectedPresenceTile` AND in `derivedStatus`, so Home/Away used to set a
   * colleague-visible status the server accepted, the tile ignored and the
   * viewer never saw: the panel said "In office" and the API said "Working
   * From Home". The write is refused instead, and the refusal says why. The
   * two other dead ends — a status this customer has not enabled, and a map
   * this tenant cannot reach — say so too rather than returning quietly.
   */
  protected async pickPresence(tile: PresenceTile): Promise<void> {
    if (tile === 'office') {
      if (this.hasDeskToday()) return;
      // Office is a status in its own right (Nath, 3 Oct 2026): set it, then
      // offer a desk where the map can book one. A desk booked later today
      // replaces the in-office status.
      if (!this.profileData.inOfficeToday()) {
        const result = await this.profileData.setInOffice();
        // Refused (not offered here, the office closed): say why, in the
        // server's words. The desk offer stands; a desk is another way in.
        if (!result.ok) this.refuse('pp-presence-office-refused', result.error ?? this.copy.officeNotSet);
      }
      if (this.canBookDesk()) this.goToDeskBooking();
      return;
    }

    // The booking wins whatever we do next, so writing the status would only
    // put the server and this tile on different answers.
    if (this.hasDeskToday()) {
      this.refuse('pp-presence-desk-booked', this.copy.deskBookedBlocksStatus);
      return;
    }

    if (this.presence() === tile) {
      await this.profileData.clearStatus();
      return;
    }
    const name = statusNameForTile(tile, this.availableStatusNames());
    const status = name
      ? this.profileData.availableStatuses().find(s => s.name === name)
      : undefined;
    if (!status) {
      this.refuse('pp-presence-unavailable', this.copy.statusNotOffered);
      return;
    }
    await this.profileData.setStatus(status._id);
  }

  /** Say no out loud. A refused write that looks like a dead button is the
   *  same defect as a silent successful one. */
  private refuse(id: string, title: string): void {
    this.toast.show({ id, title, severity: 'warning', priority: 'high' });
  }

  /** Change / book a desk — the map is where desks are chosen. Guarded as
   *  well as gated: the office tile calls this without a template gate. */
  protected goToDeskBooking(): void {
    if (!this.canBookDesk()) {
      this.refuse('pp-presence-no-map', this.copy.deskBookingUnavailable);
      return;
    }
    this.shell.closeRightPanel();
    void this.router.navigateByUrl('/map');
  }

  protected goToWeek(): void {
    this.shell.closeRightPanel();
    void this.router.navigateByUrl('/home/my-week');
  }

  /** Check in to today's desk, when the booking is live and not yet used. */
  protected async checkIn(): Promise<void> {
    const status = this.currentStatus();
    if (status.bookingId && status.canCheckIn) {
      await this.bookingService.checkInBooking(status.bookingId, 'desktop');
      await this.profileData.refresh();
    }
  }

  // ── Appearance ──────────────────────────────────────────
  //
  // Paper's Appearance block draws it now (Light · Graphite · Black, with
  // every other paper behind "All themes", COMMON ruling 1). It wears and
  // writes ThemeService through the adapter in `providePaperBridges()`, which
  // also fires APPEARANCE_THEME_CHANGED, and remembers the last light and dark
  // papers under the workspace nav's key, so Light after Dark comes home to
  // the paper the viewer was on (the job `paperForSwitch` did here).

  // ── Paper's menu rows and close ─────────────────────────

  /**
   * The menu rows Paper draws: View profile, Preferences, Admin settings for
   * an admin, and Sign out, separated, last. The rows Paper's list cannot draw
   * (the workspace switcher and its list, Send feedback with its dot, Install,
   * Update) are the app's own, in the `menu` slot between the two.
   */
  protected readonly menuActions = computed<PaperProfileAction[]>(() => {
    void this.localeService.locale();
    const t = (key: string) => this.transloco.translate(key);
    return [
      { id: 'profile', label: this.copy.viewProfile, icon: 'person' },
      {
        id: 'preferences',
        label: t('profile.profile_page.nav.section_preferences'),
        hint: this.copy.preferencesHint,
        icon: 'settings',
      },
      ...(this.isAdmin() ? [{ id: 'admin', label: t('shell.profile_panel.admin_settings'), icon: 'shield' }] : []),
      { id: 'sign-out', label: t('shell.profile_panel.sign_out'), icon: 'logout', separated: true },
    ];
  });

  /** A Paper row was chosen. Paper has already asked to close (`openChange`). */
  protected onMenuAction(id: string): void {
    switch (id) {
      case 'profile': this.navigateToProfile(); break;
      case 'preferences': this.navigateToPreferences(); break;
      case 'admin': this.navigateToAdmin(); break;
      case 'sign-out': void this.onLogout(); break;
    }
  }

  /** Paper's close button, or a chosen row: this popover closes the way it always has. */
  protected onPanelOpenChange(open: boolean): void {
    if (!open) this.shell.closeRightPanel();
  }

  // ── Avatar style ────────────────────────────────────────
  //
  // TWO STYLES, BOTH BACKED BY ONE FIELD THAT ALREADY EXISTS. `picture` set
  // is the photo; `picture` cleared is the initials mark every avatar in this
  // app already falls back to. There is no `avatarStyle` column and no
  // animated-avatar asset anywhere in this codebase, so the reference's third
  // tile (GIF) and its "animated styles pause with reduced motion" caption
  // are not built — see the long note in the template and this lane's report.
  //
  // The lit tile is DERIVED from the field rather than stored beside it. A
  // stored preference and a stored picture are two answers to one question,
  // and the pair goes out of step the first time a photo is uploaded from
  // `/profile/account` (which does not know this control exists).

  protected readonly savingAvatarStyle = signal(false);

  /** Which style is in effect, read off the only field that decides it. */
  protected readonly avatarStyle = computed<'photo' | 'initials'>(
    () => (this.profilePicture() ? 'photo' : 'initials'),
  );

  protected readonly avatarStyleOptions = computed<PaperSegmentedControlOption[]>(() => {
    void this.localeService.locale();
    return [
      { value: 'photo', label: this.copy.avatarStylePhoto },
      { value: 'initials', label: this.copy.avatarStyleInitials },
    ];
  });

  /**
   * Switch style.
   *
   * Photo with no photo is not a refusal — it is the upload, because that is
   * what choosing it means. Initials is a real write that clears `picture`,
   * and the tile only moves once the server has accepted it: `avatarStyle()`
   * reads the field, so a failed save leaves the lit tile where it was and
   * says so, rather than showing an optimistic state the next reload undoes.
   */
  protected async pickAvatarStyle(value: string): Promise<void> {
    const style: 'photo' | 'initials' = value === 'photo' ? 'photo' : 'initials';
    if (this.savingAvatarStyle()) return;
    if (style === this.avatarStyle()) return;

    if (style === 'photo') {
      this.avatarOverlayOpen.set(true);
      return;
    }

    this.savingAvatarStyle.set(true);
    try {
      const ok = await this.profileApi.updateProfile({ picture: '' });
      if (ok) {
        // NO ANALYTICS EVENT, deliberately. There is no declared event for
        // this action, and `ANALYTICS_EVENTS` lives in `core/analytics/` —
        // outside this lane. Firing `APPEARANCE_THEME_CHANGED` with
        // `from: 'avatar-photo'` would put a non-theme through a typed theme
        // schema, which is the "dynamic event name" anti-pattern wearing a
        // constant. OWED: `emp.profile.avatar-style.changed`.
        this.profileData.updateProfilePicture('');
      } else {
        this.toast.show({
          id: 'pp-avatar-style',
          // Same borrowed generic-failure string the visibility switch uses:
          // this lane cannot add i18n keys, and a write that silently does
          // nothing is worse than a borrowed sentence.
          title: this.transloco.translate('shared.dynamic_digest_dialog.error_generic'),
          severity: 'warning',
          priority: 'high',
        });
      }
    } catch (err) {
      logError('ProfilePanel', err);
    } finally {
      this.savingAvatarStyle.set(false);
    }
  }

  // ── Visible to colleagues ───────────────────────────────

  /**
   * Is there a viewer to read a visibility state OFF? The other half of the
   * sentence below, and it was missing.
   *
   * `viewer()?.hideFromPresence !== true` is `true` when `viewer()` is null,
   * so with nothing loaded the switch rendered ON — an unmeasured claim that
   * colleagues can currently see you, on a control `onVisibilityChange()`
   * refuses to act on (`if (!viewer) return`). A switch is a statement of
   * current state, and there was no state. The row is not rendered until
   * there is a viewer to render it from.
   */
  protected readonly presenceKnown = computed(
    () => this.viewerIdentity.viewer() !== null,
  );

  /**
   * Presence visibility. The wire field is the INVERSE (`hideFromPresence`),
   * and absent/falsy ON A LOADED VIEWER means visible — a viewer whose profile
   * predates the field must not read as hidden. Only meaningful behind
   * `presenceKnown()`.
   */
  protected readonly presenceVisible = computed(
    () => this.viewerIdentity.viewer()?.hideFromPresence !== true,
  );

  protected readonly savingVisibility = signal(false);

  /**
   * Save immediately — this surface has no save bar, so the switch IS the
   * commit. The cached viewer is patched only after the mutation succeeds,
   * which also means a failed save leaves the switch showing the truth
   * rather than an optimistic lie.
   */
  protected async onVisibilityChange(visible: boolean): Promise<void> {
    const viewer = this.viewerIdentity.viewer();
    if (!viewer || this.savingVisibility()) return;
    const hide = !visible;
    if (hide === (viewer.hideFromPresence === true)) return;
    this.savingVisibility.set(true);
    try {
      const ok = await this.profileApi.updateProfile({ hideFromPresence: hide });
      if (ok) {
        this.viewerIdentity.setViewer({ ...viewer, hideFromPresence: hide });
      } else {
        this.toast.show({
          id: 'pp-presence-visibility',
          // Reusing the shared generic-failure string: this lane cannot add
          // i18n keys, and a silent no-op after the switch springs back is
          // worse than a borrowed sentence.
          title: this.transloco.translate('shared.dynamic_digest_dialog.error_generic'),
          severity: 'warning',
          priority: 'high',
        });
      }
    } catch (err) {
      logError('ProfilePanel', err);
    } finally {
      this.savingVisibility.set(false);
    }
  }

  // ── Workspace switching — THE ONLY ENTRY POINT ──────────
  //
  // The shell bar's `<im-org-switcher>` is gone (deleted on 6 Oct 2026, when
  // nothing rendered it any more). Two controls switched tenant; the bar's was an
  // unlabelled building mark sitting one slot from the site chip's pin, so
  // the header carried two building icons for two different nouns. This row
  // is what `41-profile-panel` draws, and it is now the whole story.
  //
  // Three behaviours came ACROSS with it, and the move is only a move
  // because all three did:
  //   1. the search box past the disclosure threshold — via the shared
  //      `shouldShowSearch` / `filterCustomers`, not a second match rule;
  //   2. the PII contract on the search event (length + counts, never the
  //      text) — `onWorkspaceQueryChange` below;
  //   3. the customer-scoped `localStorage` purge — already inside
  //      `PlatformCustomerService.switchTo()`, which this row already called.

  /** Accessible customers; the row only renders when there's >1. */
  protected readonly switchableCustomers = this.platformCustomers.customers;
  /** The customer the session is currently scoped to (read once; null = none). */
  protected readonly currentCustomerId = this.platformCustomers.currentCustomerId();
  /** Id mid-switch (drives the row spinner + disables re-entry). */
  protected readonly switchingCustomerId = signal<string | null>(null);
  /** Workspace logos that failed to load — those rows fall back to the icon. */
  protected readonly workspaceLogoErrors = signal<Record<string, boolean>>({});

  /** Current name-filter text. Only meaningful once `showWorkspaceSearch()`. */
  protected readonly workspaceQuery = signal('');

  /**
   * Offer the name filter only once the list outgrows a glance. Below the
   * threshold a search box is a text field in front of four rows, and inside
   * a menu that already scrolls it costs more height than it saves.
   */
  /** Past `SWITCHER_DIALOG_THRESHOLD` the row opens the searchable dialog. */
  protected readonly useWorkspaceDialog = computed(() =>
    shouldUseDialog(this.switchableCustomers().length),
  );
  private readonly workspaceDialog = inject(WorkspaceSwitcherDialogService);

  protected readonly showWorkspaceSearch = computed(() =>
    shouldShowSearch(this.switchableCustomers().length, SWITCHER_SEARCH_THRESHOLD),
  );

  /** The list the rows render, filtered case-insensitively by name. An empty
   *  query returns everything, so the unfiltered path has no special case. */
  protected readonly filteredWorkspaces = computed(() =>
    filterCustomers(this.switchableCustomers(), this.workspaceQuery()),
  );

  /** Debounce timer for the SEARCH event. Cleared on destroy — the panel is
   *  unmounted every time it closes, and a pending timer would fire an event
   *  for a surface that no longer exists. */
  private workspaceSearchTimer: ReturnType<typeof setTimeout> | null = null;

  /** A logo that 404s falls back to the building icon rather than to the
   *  browser's broken-image glyph. */
  protected onWorkspaceLogoError(customerId: string): void {
    this.workspaceLogoErrors.update(m => ({ ...m, [customerId]: true }));
  }

  /** Collapse and reset. Reopening starts from the full list, never from
   *  someone else's half-typed filter. */
  protected toggleWorkspaceList(): void {
    // Many workspaces (RDx support staff reach every customer): a searchable
    // dialog, not a list scrolling inside the panel.
    // Close the panel first: while a popover is open the popover band takes
    // Escape in document capture, so the dialog's own Escape never fired.
    // Open two frames later — the band hands focus back to the avatar on a
    // microtask and again a frame after, which would pull it out of the
    // dialog's search field.
    if (this.useWorkspaceDialog()) {
      const dialog = this.workspaceDialog;
      // The dialog grows out of the control this panel came from (the avatar),
      // which is still on screen once the panel has gone.
      const origin = this.popoverBand.triggerFor('shell.profile');
      this.shell.closeRightPanel();
      requestAnimationFrame(() => requestAnimationFrame(() => dialog.open(origin)));
      return;
    }
    const next = !this.workspaceListOpen();
    this.workspaceListOpen.set(next);
    if (!next) this.workspaceQuery.set('');
  }

  /**
   * Update the filter, and schedule the debounced SEARCH event.
   *
   * ⚠️ THE TYPED TEXT NEVER LEAVES THE BROWSER. Workspace names are customer
   * names — the organisations this user can reach — so the query is somebody's
   * client list. The event carries its LENGTH and the two counts that make it
   * analysable (did the search narrow anything?) and nothing else; the input
   * itself is `ph-mask`ed against session replay. Adding the string here would
   * be a one-word change and a privacy incident.
   */
  protected onWorkspaceQueryChange(value: string): void {
    this.workspaceQuery.set(value);
    if (this.workspaceSearchTimer) clearTimeout(this.workspaceSearchTimer);
    this.workspaceSearchTimer = setTimeout(() => {
      const len = this.workspaceQuery().trim().length;
      if (len > 0) {
        this.analytics.capture(ANALYTICS_EVENTS.ORG_SWITCHER_SEARCHED, {
          query_length: len,
          customer_count: this.switchableCustomers().length,
          result_count: this.filteredWorkspaces().length,
        });
      }
    }, WORKSPACE_SEARCH_DEBOUNCE_MS);
  }

  /** Name of the workspace the session is scoped to, or `null` when the id
   *  matches nothing in the accessible list. The row then carries no value
   *  rather than an empty one holding a slot in the flex line. */
  protected readonly currentCustomerName = computed(
    () => this.switchableCustomers().find(c => c.id === this.currentCustomerId)?.name ?? null,
  );

  protected async switchCustomer(id: string): Promise<void> {
    if (!id || id === this.currentCustomerId || this.switchingCustomerId()) return;
    this.switchingCustomerId.set(id);
    // Pre-reload cleanup (map prefs reset, customer-scoped storage, persisted
    // Apollo cache purge) is centralised inside switchTo().
    const ok = await this.platformCustomers.switchTo(id, 'profile');
    // On success the app hard-reloads; only reset the spinner on failure.
    if (!ok) this.switchingCustomerId.set(null);
  }

  // ── Aria ────────────────────────────────────────────────

  /** Localised "Change profile picture for <name>" aria-label. `null` when the
   *  name is not known — the only key available interpolates {name}, and
   *  "Change profile picture for User" would announce the same fabrication the
   *  header no longer renders. The button lives inside the identity gate, so
   *  in practice the name is always there by the time this is read. */
  protected readonly avatarAriaLabel = computed(() => {
    this.localeService.locale(); // track for reactivity
    const name = this.userName();
    if (!name) return null;
    return this.transloco.translate('shell.profile_panel.change_profile_picture_for', { name });
  });

  /** Localised "Status: <label>" aria-label. */
  protected readonly statusAriaLabel = computed(() => {
    this.localeService.locale(); // track for reactivity
    return this.transloco.translate('shell.profile_panel.status_label', { label: this.currentStatus().label });
  });

  constructor() {
    // Load panel data on open. untracked() stops signal writes inside the
    // loaders re-triggering us.
    effect(() => {
      const panel = this.shell.rightPanel();
      if (panel === 'profile') {
        untracked(() => {
          this.profileData.loadAll();
          // A switch that failed earlier, elsewhere, is not news on open.
          this.platformCustomers.clearSwitchError();
          // Best-effort: load accessible customers so the "Switch workspace"
          // row appears for internal users with >1 customer (else hidden).
          // This is now the ONLY surface that asks for the list, so the load
          // has to happen here — the bar's switcher used to do it at boot.
          void this.platformCustomers.load();
        });
      }
    });

    // The panel is destroyed on every close (it is one arm of the shell's
    // @switch slot), so a pending debounce would outlive the surface it
    // measured and report a search nobody is looking at.
    inject(DestroyRef).onDestroy(() => {
      if (this.workspaceSearchTimer) clearTimeout(this.workspaceSearchTimer);
    });
  }

  // ── Profile picture ─────────────────────────────────────

  protected triggerFileUpload(): void {
    // Use DOM query instead of viewChild because #fileInput is inside a conditional @if block
    // and viewChild may not have resolved yet
    const input = document.querySelector<HTMLInputElement>('#pp-file-input');
    input?.click();
  }

  /** Raster image types accepted for profile photos. SVG is deliberately
   *  excluded — it can carry embedded script. */
  private static readonly PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  private static readonly PHOTO_MAX_BYTES = 5 * 1024 * 1024;

  protected async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    // Client-side guard — the server validates too, but the template's
    // accept="image/*" is only a picker hint and trivially bypassed, so
    // don't let obviously invalid uploads reach the network at all.
    if (!ProfilePanelComponent.PHOTO_TYPES.includes(file.type)) {
      this.toast.show({
        id: 'pp-photo-type',
        title: this.transloco.translate('shell.profile_panel.photo_type_invalid'),
        severity: 'warning',
        priority: 'high',
      });
      input.value = '';
      return;
    }
    if (file.size > ProfilePanelComponent.PHOTO_MAX_BYTES) {
      this.toast.show({
        id: 'pp-photo-size',
        title: this.transloco.translate('shell.profile_panel.photo_too_large'),
        severity: 'warning',
        priority: 'high',
      });
      input.value = '';
      return;
    }

    this.uploadingPhoto.set(true);

    try {
      const url = await this.issuesApi.uploadFile(file);
      if (url) {
        // Optimistic local update so the new avatar appears instantly,
        // then persist via updateProfile so it survives a reload. The
        // mutation patches ProfileDataService on success.
        this.profileData.updateProfilePicture(url);
        const ok = await this.profileApi.updateProfile({ picture: url });
        if (ok) this.avatarOverlayOpen.set(false);
      }
    } catch (err) {
      logError('Profile', err);
    } finally {
      this.uploadingPhoto.set(false);
      input.value = ''; // Reset for re-selection
    }
  }

  // ── Menu ────────────────────────────────────────────────

  protected navigateToProfile(): void {
    this.shell.closeRightPanel();
    this.router.navigateByUrl('/profile');
  }

  protected navigateToPreferences(): void {
    this.shell.closeRightPanel();
    this.router.navigateByUrl('/profile/appearance');
  }

  /** Replay the deferred browser install prompt (PWA). */
  protected installApp(): void {
    void this.pwaInstall.promptInstall('profile-panel');
  }

  protected navigateToAdmin(): void {
    this.shell.closeRightPanel();
    // The admin area now lives at /admin; this entry is already gated on
    // isAdmin() (canManageSomething()), so /admin is always reachable here.
    this.router.navigateByUrl('/admin');
  }

  protected async onLogout(): Promise<void> {
    // Best-effort: unregister this device's push token before the session
    // goes away. Never block logout on it (the mutation needs the session,
    // so run it first, but swallow any failure).
    try {
      await this.pushRegistration.disable();
    } catch {
      /* push teardown is best-effort — never block sign-out */
    }
    // Stop cross-device theme write-through and reset map prefs to defaults so
    // the next (possibly anonymous) viewer on this device doesn't inherit the
    // signed-out user's synced theme. Best-effort — never block sign-out.
    try {
      this.mapPrefsSync.reset();
    } catch {
      /* prefs reset is best-effort */
    }
    // Same for the synced profile settings — clears dietary + vehicles on
    // this device (they live on the server and return on next sign-in).
    try {
      this.profileSettingsSync.reset();
    } catch {
      /* prefs reset is best-effort */
    }
    await this.sessionService.logout();
  }
}
